// Single source of truth for combining the price layers (base variant + bracket
// addon + rip cut) into a final total. Declared defensively here too since this
// script can load before or after option-width-prime-range.js depending on
// which product template renders it.
window.ScaffPricing = window.ScaffPricing || {
  totalCents({ baseCents = 0, addonCents = 0, ripCutCents = 0 } = {}) {
    return (baseCents || 0) + (addonCents || 0) + (ripCutCents || 0);
  }
};

class AddonBracketSelector extends HTMLElement {
  constructor() {
    super();
    this.productData = null;
    this.selectedVariant = null;
    this.selectedOptions = {};
    this.variantMap = new Map();
    this.addonVariantImages = {};
    this.addonProductsByQuantity = {};
    this.matchedAddonMissing = false;
    this.isEnabled = false;
    this._updatingPrice = false;
    this._priceObserver = null;
    this.init();
  }

  init() {
    this.loadData();
    this.attachEvents();
    this.listenForBasePriceChanges();
    this.observePriceElement();
    const toggle = this.querySelector('[data-addon-toggle]');
    if (toggle && toggle.checked) {
      this.toggleAddon(true);
    } else {
      // Toggle starts off: disable radios, snap to first option, and emit so
      // the 3D model and cart data are synced from the very first render.
      // this.querySelectorAll('[data-addon-input]').forEach(r => (r.disabled = true));
      this.selectFirstOption();
      this.emitAddonUpdate();
    }
    this.updatePrice();
    this.syncWithBracketSelection();
  }

  syncWithBracketSelection() {
    const bracketList = document.querySelector('.option-bracket__list');
    if (!bracketList || !bracketList.children.length) return;

    const activeBracketItem = bracketList.querySelector('[data-bracket-item].active') || bracketList.querySelector('[data-bracket-item].is-active') || bracketList.querySelector('[data-bracket-item][aria-selected="true"]');
    
    if (!activeBracketItem) return;

    const handleValue = activeBracketItem.getAttribute('data-value');
    if (!handleValue) return;

    const matchingInput = this.querySelector(`[data-addon-input][data-value-handle="${handleValue}"]`);
    
    if (!matchingInput) return;

    const inputId = matchingInput.id;
    if (inputId) {
      const label = this.querySelector(`label[for="${inputId}"]`);
      if (label) { label.click(); }
    }
  }

  loadData() {
    const dataTag = document.getElementById('addon-bracket-data');
    if (!dataTag) return;

    try {
      const data = JSON.parse(dataTag.textContent);
      this.productData = data.product || null;
      this.selectedVariant = data.selectedVariant || null;
      this.addonVariantImages = data.variantImages || {};
      this.addonProductsByQuantity = data.addonProductsByQuantity || {};

      if (this.productData?.variants) {
        this.productData.variants.forEach(variant => {
          const key = [variant.option1, variant.option2, variant.option3]
            .filter(Boolean)
            .join('|||');
          this.variantMap.set(key, variant);
        });
      }

      if (this.selectedVariant) {
        this.selectedOptions = {
          option1: this.selectedVariant.option1,
          option2: this.selectedVariant.option2,
          option3: this.selectedVariant.option3,
        };
      } else if (this.productData?.options) {
        // product.options is an array of option name strings (not objects with .values).
        // Derive first values from the first variant instead.
        const firstVariant = this.productData.variants?.[0];
        this.productData.options.forEach((_, i) => {
          this.selectedOptions[`option${i + 1}`] = firstVariant?.[`option${i + 1}`] ?? null;
        });
      }
      this.selectAddonProductForRequiredQuantity();
    } catch (_) {
      console.error('Failed to parse addon data');
    }
  }

  getRequiredBracketQuantity() {
    const model = document.querySelector('build-your-board-model');
    if (model && typeof model.getBracketCount === 'function' && typeof model.getLengthMm === 'function') {
      return model.getBracketCount(model.getLengthMm());
    }

    const slider = document.querySelector('[data-option-range-slider]');
    if (!slider) return 2;

    const lengthCm = parseFloat(slider.value);
    return this.getBracketQuantityForLength(lengthCm);
  }

  selectAddonProductForRequiredQuantity() {
    const requiredQty = this.getRequiredBracketQuantity();
    const entry = this.addonProductsByQuantity?.[requiredQty]
      ?? this.addonProductsByQuantity?.[String(requiredQty)];

    if (!entry?.product) {
      console.warn(
        `[AddonBracketSelector] No add-on product found for required bracket quantity: ${requiredQty}. Skipping add-on.`
      );
      this.matchedAddonMissing = true;
      this.productData = null;
      this.variantMap.clear();
      this.selectedVariant = null;
      return false;
    }

    this.matchedAddonMissing = false;
    const prevOptions = { ...this.selectedOptions };
    this.productData = entry.product;

    this.variantMap.clear();
    this.productData.variants?.forEach(variant => {
      const key = [variant.option1, variant.option2, variant.option3]
        .filter(Boolean)
        .join('|||');
      this.variantMap.set(key, variant);
    });

    const titleEl = this.querySelector('.addon-toggle-label');
    if (titleEl && entry.title) {
      titleEl.textContent = entry.title;
    }

    const optionKey = Object.values(prevOptions).filter(Boolean).join('|||');
    this.selectedVariant = this.variantMap.get(optionKey)
      || this.productData.variants?.find(v => v.available)
      || this.productData.variants?.[0]
      || null;

    if (this.selectedVariant) {
      this.selectedOptions = {
        option1: this.selectedVariant.option1,
        option2: this.selectedVariant.option2,
        option3: this.selectedVariant.option3,
      };
    }

    if (this.selectedVariant?.id) {
      this.setAttribute('data-addon-variant', this.selectedVariant.id);
    }

    return true;
  }

  attachEvents() {
    const toggle = this.querySelector('[data-addon-toggle]');
    if (toggle) { toggle.addEventListener('change', e => this.toggleAddon(e.target.checked)); }

    const slider = document.querySelector('[data-option-range-slider]');
    if (slider) {
      slider.addEventListener('input', () => this.updatePrice());
      slider.addEventListener('change', () => this.updatePrice());
    }
    document.addEventListener('length-changed', () => this.updatePrice());
    document.addEventListener('rip-cut:change', () => this.updatePrice());

    this.addEventListener('change', e => {
      if (!e.target.matches('[data-addon-input]')) return;
      this.selectedOptions[e.target.dataset.option] = e.target.dataset.value;
      // Always emit so 3D model color stays in sync regardless of toggle state
      this.slideshow = document.querySelector('.product__grid[data-product-slideshow]');
      const { payload, mediaElements, isExisting } = this.emitAddonUpdate();
      if (this.isEnabled) {
        this.handleMediaGallery(payload, mediaElements, isExisting);
      }
      this.updatePrice();
    });
  }

  handleMediaGallery(payload, mediaElements, isExisting) {
    if (!mediaElements || !payload.media?.id) { console.log('[handleBracketAddon] No valid media elements or media ID, proceeding with normal flow'); return false; }
    if (isExisting) { this.switchToExistingBracketMedia(payload.media.id); } else { this.addNewBracketMedia(mediaElements, payload.media.id); } return true;
  }

  switchToExistingBracketMedia(mediaId) {
    this.slideshow.dispatchEvent(new CustomEvent('theme:image:change', { detail: { id: mediaId } }));
  }
  
  addNewBracketMedia(mediaElements, mediaId) {
    const flickitySlider = this.slideshow.querySelector('.flickity-slider');
    if (flickitySlider && mediaElements.slider) { flickitySlider.appendChild(mediaElements.slider); }

    if (mediaElements.thumbnail) {
      const thumbsContainer = document.querySelector('[data-product-thumbs]');
      document.querySelectorAll('[data-slideshow-thumbnail]').forEach(t => t.classList.remove('is-activated'));
      this.slideshow.querySelectorAll('[data-media-slide]').forEach(s => s.classList.remove('is-selected'));

      if (thumbsContainer) {
        thumbsContainer.appendChild(mediaElements.thumbnail);
        mediaElements.thumbnail.addEventListener('click', () => {
          this.slideshow.dispatchEvent(new CustomEvent('theme:image:change', {
            detail: { id: mediaId }
          }));
        });
      }
    }

    this.slideshow.dispatchEvent(new CustomEvent('theme:media:reinit', { detail: { selectMediaId: mediaId } }));
    setTimeout(() => { this.slideshow.dispatchEvent(new CustomEvent('theme:image:change', { detail: { id: mediaId } })); }, 150);
  }

  toggleAddon(enabled) {
    this.isEnabled = enabled;
    const options = this.querySelector('[data-options]');
    const radios = this.querySelectorAll('[data-addon-input]');

    // Always update radios and selection state regardless of whether the
    // options panel element exists (guard only the panel DOM manipulation).
    if (enabled) {
      if (options) {
        options.style.setProperty('--options-height', `${options.scrollHeight}px`);
        options.classList.add('enabled');
      }
      radios.forEach(r => (r.disabled = false));
    } else {
      if (options) {
        options.classList.remove('enabled');
      }
      radios.forEach(r => (r.disabled = true));
      // Full reset: snap every option group back to its first value.
      this.selectFirstOption();
    }

    this.emitAddonUpdate();
    this.updatePrice();
  }

  selectFirstOption() {
    if (!this.productData?.options) return;

    // product.options is an array of option name strings in Shopify JSON.
    // Pull first values from the first variant — variants are ordered by option
    // position so variants[0] always holds the first value for each option.
    const firstVariant = this.productData.variants?.[0];
    if (!firstVariant) return;

    // 1. Update the internal state — single source of truth for variant lookup
    this.productData.options.forEach((_, i) => {
      this.selectedOptions[`option${i + 1}`] = firstVariant[`option${i + 1}`] ?? null;
    });

    // 2. Sync radio checked state per option group.
    //    Use dataset comparison (not a CSS quoted-string selector) so that
    //    multi-word values like "Steel Blue" are matched correctly.
    this.productData.options.forEach((_, i) => {
      const optionKey = `option${i + 1}`;
      const firstValue = this.selectedOptions[optionKey];
      const groupRadios = [...this.querySelectorAll(`[data-addon-input][data-option="${optionKey}"]`)];
      groupRadios.forEach(r => (r.checked = false));
      const firstRadio = groupRadios.find(r => r.dataset.value === firstValue) ?? groupRadios[0];
      if (firstRadio) {
        firstRadio.checked = true;
        // Click the label so swatch/active-state handlers fire (mirrors syncWithBracketSelection)
        const label = this.querySelector(`label[for="${firstRadio.id}"]`);
        if (label) label.click();
      }
    });
  }

  listenForBasePriceChanges() {
    // When option-width-prime-range (or any selector) updates the main variant,
    // it sets input[name="id"] and dispatches an 'input' event.
    // The theme then renders the selected variant's canonical price.
    const variantInput = document.querySelector('input[type="hidden"][name="id"]');
    if (variantInput) {
      variantInput.addEventListener('input', () => {
        // The theme owns the variant price update. Wait for its DOM render;
        // observePriceElement will then re-apply the bracket calculation.
        setTimeout(() => this.updatePrice(), 120);
      });
    }
  }

  observePriceElement() {
    // Watch the price element for external updates (from theme JS or option-width-prime-range.js).
    // Whenever the theme rewrites the price, immediately re-apply the addon price on top.
    const priceEl = document.querySelector('[data-product-price]');
    if (!priceEl) return;

    this._priceObserver = new MutationObserver(() => {
      if (this._updatingPrice) return; // our own write — skip to avoid loop

      // Preserve compatibility with any handler that writes raw cents.
      const text = (priceEl.textContent || '').trim();
      if (/^\d+$/.test(text)) {
        priceEl.setAttribute('data-base-price', text);
      }

      this.updatePrice();
    });

    this._priceObserver.observe(priceEl, { childList: true, characterData: true, subtree: true });
  }

  updatePrice() {
    const priceEl = document.querySelector('[data-product-price]');
    if (!priceEl) return;
    const rawBase = priceEl.getAttribute('data-base-price');
    if (rawBase === null) return; // attribute not present, leave the element untouched
    this.selectAddonProductForRequiredQuantity();
    const basePriceCents = parseInt(rawBase, 10) || 0;
    const addonVariant = this.getAddonVariant();
    const requiredBracketQuantity = this.getRequiredBracketQuantity();
    const cartQuantity = this.getAddonQuantity();
    // Only include the addon price when the bracket toggle is checked.
    const addonPriceCents = (this.isEnabled && addonVariant) ? (addonVariant.price || 0) : 0;
    const addonTotalCents = addonPriceCents * cartQuantity;
    const ripCutCents = window.ripCutState?.active
      ? (window.ripCutState.totalPriceCents || 0)
      : 0;
    const totalCents = window.ScaffPricing.totalCents({
      baseCents: basePriceCents,
      addonCents: addonTotalCents,
      ripCutCents
    });

    window.bracketAddonState = {
      enabled: this.isEnabled,
      unitCents: addonPriceCents,
      quantity: cartQuantity,
      requiredBracketQuantity,
      totalCents: this.isEnabled && addonVariant ? addonTotalCents : 0,
      variantId: addonVariant?.id || null,
      title: addonVariant?.title || addonVariant?.name || null
    };
    document.dispatchEvent(new CustomEvent('bracket-addon:change', {
      detail: window.bracketAddonState,
      bubbles: true
    }));

    // themeCurrency is scoped inside theme.js's IIFE — access it via the global themeVendor object.
    const currencyFormatter = themeVendor?.themeCurrency;
    if (!currencyFormatter) return; // vendor bundle not loaded yet; MutationObserver will retry
    const formatted = window.theme?.settings?.currency_code_enable
      ? currencyFormatter.formatMoney(totalCents, theme.moneyFormat) + ` ${theme.currencyCode}`
      : currencyFormatter.formatMoney(totalCents, theme.moneyFormat);
    // Guard against our own write triggering the MutationObserver
    this._updatingPrice = true;
    priceEl.innerHTML = formatted;
    // Reset after a macrotask so all pending MO callbacks fire first with the guard set
    setTimeout(() => { this._updatingPrice = false; }, 0);
  }

  getAddonVariant() {
    const key = Object.values(this.selectedOptions).filter(Boolean).join('|||');
    return this.variantMap.get(key) || null;
  }

  getAddonQuantity() {
    return 1;
  }

  getBracketQuantityForLength(lengthCm) {
    if (!lengthCm || lengthCm < 30) return 2;
    if (lengthCm <= 149) return 2;
    if (lengthCm <= 299) return 3;
    return 4;
  }

  getMainVariantId() {
    const input = document.querySelector('input[name="id"]');
    return input?.value || new URLSearchParams(location.search).get('variant');
  }

  generateStableId(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return `${Math.abs(hash)}`;
  }

  getMatchedMedia(mainVariantId) {
    const images = this.addonVariantImages?.[mainVariantId];
    if (!Array.isArray(images) || !images.length) return null;

    for (const value of Object.values(this.selectedOptions)) {
      const match = images.find(img => img.label?.toLowerCase() === value?.toLowerCase());
      if (match?.image?.url) {
        // Use existing ID or generate stable one from URL
        const stableId = match.image.id || this.generateStableId(match.image.url);
        return {
          id: stableId,
          url: match.image.url,
          aspectRatio: match.image.aspect_ratio || '1',
          source: 'variant-metafield'
        };
      }
    }

    const fallback = images[0];
    if (fallback?.image?.url) {
      // Use existing ID or generate stable one from URL
      const stableId = fallback.image.id || this.generateStableId(fallback.image.url);
      return {
        id: stableId,
        url: fallback.image.url,
        aspectRatio: fallback.image.aspect_ratio || '1',
        source: 'variant-metafield'
      };
    }

    return null;
  }

  createMediaElements(media) {
    if (!media?.url) return null;

    const templatesScript = document.getElementById('addon-media-templates');
    if (!templatesScript) return null;

    const imageId = media.id || this.generateStableId(media.url);
    const alt = 'Add-on bracket image';

    const templatesHtml = templatesScript.textContent.replaceAll('${image_id}', imageId).replaceAll('${variant_id}', 'addon').replaceAll('${media_aspect_ratio}', media.aspectRatio || '1').replaceAll('${image_url}', media.url).replaceAll('${thumb_url}', media.url).replaceAll('${alt}', alt);

    const parser = new DOMParser();
    const doc = parser.parseFromString(templatesHtml, 'text/html');

    const sliderTemplate = doc.getElementById('addon-media-template-slider');
    const thumbnailTemplate = doc.getElementById('addon-media-template-thumbnail');

    return { slider: sliderTemplate?.content.firstElementChild, thumbnail: thumbnailTemplate?.content.firstElementChild };
  }

  emitAddonUpdate() {
    this.selectAddonProductForRequiredQuantity();
    const mainVariantId = this.getMainVariantId();
    const addonVariant = this.getAddonVariant();
    const media = this.getMatchedMedia(mainVariantId);

    // Check if media elements already exist using the stable ID
    let mediaElements = null;
    let isExisting = false;
    const imageId = media?.id || (media?.url ? this.generateStableId(media.url) : null);

    if (imageId) {
      const existingSlider = document.querySelector(`[data-media-id="${imageId}"]`);
      const existingThumbnail = document.querySelector(`[data-media-select="${imageId}"]`);

      if (existingSlider || existingThumbnail) {
        mediaElements = { slider: existingSlider, thumbnail: existingThumbnail };
        isExisting = true;
      } else {
        mediaElements = this.createMediaElements(media);
        isExisting = false;
      }
    }

    const payload = {
      product_variant_id: mainVariantId || null,
      addon: {
        enabled: this.isEnabled,
        variant_id: addonVariant?.id?.toString() || null,
        options: { ...this.selectedOptions }
      },
      media: media || {}
    };

    document.dispatchEvent( new CustomEvent('addon:update-gallery', { detail: payload }));
    return { payload, mediaElements, isExisting };
  }
}

if (!customElements.get('addon-bracket-selector')) {
  customElements.define('addon-bracket-selector', AddonBracketSelector);
}
